import { useCallback, useState } from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { ChatInput } from './ChatInput'
import { ConversationHistory } from './ConversationHistory'
import { MessageList } from './MessageList'
import { useAiChatBotStore } from '../core/store/useAiChatBotStore'
import { useConversationStore } from '../core/store/useConversationStore'
import { theme } from '@/Atomic/assets/theme'
import { Button } from '@/Atomic/Molecules/Button'

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.space.m} ${theme.space.m} ${theme.space.m} ${theme.space.xl};
  background: ${theme.colors.background.default};
  border-bottom: 1px solid ${theme.colors.border.line};
  flex-shrink: 0;
  gap: ${theme.space.s};
`

const Title = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${theme.colors.text.primary};
  flex: 1;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space['2xs']};
`

const ContentArea = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const HistoryOverlay = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  overflow: hidden;
  max-height: ${({ isOpen }) => (isOpen ? '260px' : '0')};
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${theme.colors.background.default};
  border-bottom: 1px solid ${theme.colors.border.line};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
`

const Footer = styled.div`
  padding: ${theme.space.s} ${theme.space.xl};
  border-top: 1px solid ${theme.colors.border.line};
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  text-align: center;
  line-height: 1.4;
  flex-shrink: 0;
  background: ${theme.colors.background.default};
`

const FooterLink = styled.a`
  color: ${theme.colors.link.default};
  text-decoration: underline;

  &:hover {
    color: ${theme.colors.link.hover};
  }
`

export function AiChatBotPanelContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const { activeConfig } = useAiChatBotStore(({ activeConfig }) => ({
    activeConfig,
  }))
  const { setActiveId } = useAiChatBotStore.getState()
  const { init, reset } = useConversationStore.getState()
  const [showHistory, setShowHistory] = useState(false)

  const handleSelectConversation = useCallback(
    (id: string) => {
      setShowHistory(false)
      setActiveId(id)
      if (activeConfig) {
        init(id, activeConfig)
      }
    },
    [activeConfig, setActiveId, init],
  )

  const handleNewConversation = useCallback(() => {
    setShowHistory(false)
    reset()
  }, [reset])

  return (
    <>
      <Header>
        <Title>{t('components.aiChatBot.title')}</Title>
        <HeaderActions>
          <Button
            icon="add-circle"
            variant="outlined"
            size="sm"
            onClick={handleNewConversation}
            aria-label={t('components.aiChatBot.history.newChat')}
          >
            <span className="d-none d-md-inline">
              {t('components.aiChatBot.history.newChat')}
            </span>
          </Button>
          <Button
            icon="clock"
            variant="icon"
            size="sm"
            onClick={() => setShowHistory((v) => !v)}
            ariaLabel={t('components.aiChatBot.history.chatHistory')}
          />
          <Button
            icon="chevron-right"
            variant="icon"
            size="sm"
            onClick={onClose}
            ariaLabel={t('components.aiChatBot.close')}
          />
        </HeaderActions>
      </Header>
      <ContentArea>
        <HistoryOverlay isOpen={showHistory}>
          <ConversationHistory
            onSelect={handleSelectConversation}
            onClose={() => setShowHistory(false)}
          />
        </HistoryOverlay>
        <MessageList />
        <ChatInput />
        <Footer>
          {t('components.aiChatBot.privacy.policy')}{' '}
          <FooterLink href="#" onClick={(e) => e.preventDefault()}>
            {t('components.aiChatBot.privacy.link')}
          </FooterLink>
        </Footer>
      </ContentArea>
    </>
  )
}
